import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPurchaseById, getProducts, sendPrintRequest, getDeliveryGuides, getDeliverySummaryForPurchase } from '../api';
import { FiPrinter } from 'react-icons/fi';
import useCurrentUser from '../hooks/useCurrentUser';
import { hasPermission } from '../utils/permissions';
import { normalizePaginatedResponse } from '../utils/pagination';
import InlineLoading from '../components/InlineLoading';
import { getPurchaseItems } from '../utils/purchaseItems';
import { notify } from '../utils/alertBus';
import useProductEvents from '../hooks/useProductEvents';
import { applyProductEvent } from '../utils/productEventPatch';
import { getPrintMode, PRINT_MODE_REMOTE } from '../utils/printSettings';
import { CANCEL_REASON_CATEGORY_MAP } from '../constants/cancelReasonCategories';

const formatSignificantQuantity = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return '0';
    const safeValue = Math.abs(parsed) < Number.EPSILON ? 0 : parsed;
    return safeValue.toLocaleString('de-DE', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 6,
        useGrouping: false,
    });
};

const PurchaseDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [purchase, setPurchase] = useState(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deliveryGuides, setDeliveryGuides] = useState([]);
    const [deliverySummary, setDeliverySummary] = useState(null);
    const branchId = useParams().branchId || localStorage.getItem('selectedBranchId');
    const { user } = useCurrentUser();
    const canReadSales = hasPermission(user, 'sales.read');
    const canUpdateSale = hasPermission(user, 'sales.update');
    const canReadClient = hasPermission(user, 'client.read');
    const canReadProducts = hasPermission(user, 'product.read');
    const canReadGuides = hasPermission(user, 'delivery_guide.read');
    const canCreateGuide = hasPermission(user, 'delivery_guide.create');

    const refreshProducts = useCallback(async () => {
        if (!branchId || !canReadProducts) {
            if (!canReadProducts) {
                setProducts([]);
            }
            return;
        }
        try {
            const productsData = await getProducts(branchId, { pageSize: 0 });
            const normalized = normalizePaginatedResponse(productsData);
            setProducts(normalized.data || []);
        } catch (error) {
            console.error('Error refreshing products:', error);
        }
    }, [branchId, canReadProducts]);

    const handleProductEvent = useCallback((event) => {
        const nextProducts = applyProductEvent(products, event);
        if (!nextProducts) {
            refreshProducts();
            return;
        }
        if (nextProducts === products) return;
        setProducts(nextProducts);
    }, [products, refreshProducts]);

    useProductEvents(branchId, handleProductEvent);

    const handlePrint = async () => {
        try {
            if (!canReadSales) return;

            if (!branchId) {
                notify("Sucursal no definida.");
                return;
            }
            const printMode = getPrintMode();
            const useRemotePrint = printMode === PRINT_MODE_REMOTE;
            if (useRemotePrint) {
                sendPrintRequest(branchId, id);
            } else {
                const printUrl = `/branches/${branchId}/purchases/${id}/receipt/preview?autoprint=1`;
                const printWindow = window.open(printUrl, "_blank", "noopener,noreferrer");
                if (!printWindow) {
                    notify("No se pudo abrir la impresion local. Revisa el bloqueador de ventanas emergentes.");
                    return;
                }
            }
            const notification = document.createElement("div");
            notification.textContent = useRemotePrint
                ? "Imprimiendo comprobante..."
                : "Abriendo comprobante en el navegador...";
            notification.style.position = "fixed";
            notification.style.bottom = "20px";
            notification.style.right = "20px";
            notification.style.backgroundColor = "#4caf50";
            notification.style.color = "white";
            notification.style.padding = "10px";
            notification.style.borderRadius = "5px";
            notification.style.boxShadow = "0px 4px 6px rgba(0, 0, 0, 0.1)";
            document.body.appendChild(notification);

            setTimeout(() => {
                document.body.removeChild(notification);
            }, 3000);
        } catch (error) {
            console.error("Error updating purchase as paid:", error);
            notify("Error al imprimir.");
        }
    };

    useEffect(() => {
        const fetchPurchaseAndProducts = async () => {
            try {
                const [purchaseData, productsData] = await Promise.all([
                    getPurchaseById(branchId, id),
                    canReadProducts ? getProducts(branchId, { pageSize: 0 }) : Promise.resolve([])
                ]);

                if (typeof purchaseData.detailForPayment === 'string') {
                    purchaseData.detailForPayment = JSON.parse(purchaseData.detailForPayment);
                }

                setPurchase(purchaseData);
                const normalizedProducts = normalizePaginatedResponse(productsData);
                setProducts(normalizedProducts.data || []);

                const [guidesData, summaryData] = await Promise.all([
                    canReadGuides ? getDeliveryGuides(branchId, { purchaseId: id, pageSize: 0 }) : Promise.resolve([]),
                    canReadGuides ? getDeliverySummaryForPurchase(branchId, id) : Promise.resolve(null)
                ]);

                const normalizedGuides = normalizePaginatedResponse(guidesData);
                setDeliveryGuides(normalizedGuides.data || []);
                setDeliverySummary(summaryData || null);
            } catch (error) {
                console.error('Error fetching purchase:', error);
                notify('Error al cargar la compra.');
                navigate(`/branches/${branchId}/purchases`);
            } finally {
                setLoading(false);
            }
        };

        fetchPurchaseAndProducts();
    }, [id, navigate, branchId, canReadProducts, canReadGuides]);

    if (loading) {
        return (
            <p className="text-center mt-6">
                <InlineLoading />
            </p>
        );
    }

    if (!purchase) {
        return <p className="text-center mt-6 text-red-500">No se encontró la compra.</p>;
    }

    const getCancelReasonText = (entry) => {
        if (!entry) return null;
        const category = String(entry.cancelReasonCategory || '').trim().toLowerCase();
        const detail = String(entry.cancelReasonDetail || '').trim();
        if (category) {
            const categoryLabel = CANCEL_REASON_CATEGORY_MAP[category]?.label || category;
            return detail ? `${categoryLabel}: ${detail}` : categoryLabel;
        }
        const legacyReason = String(entry.cancelReason || '').trim();
        return legacyReason || null;
    };
    const isCancelled = purchase.isDeleted || String(purchase.saleStatus || '').toLowerCase() === 'cancelled';
    const cancelReasonText = getCancelReasonText(purchase);
    const purchaseItems = getPurchaseItems(purchase);
    const isClosed = purchase.paymentTerm === 'credit'
        || purchase.paymentMethod === 'crédito'
        || purchase.paymentStatus === 'paid'
        || purchase.isPaid
        || isCancelled;
    const itemsSubtotal =
        typeof purchase.itemsSubtotal === 'number'
            ? purchase.itemsSubtotal
            : purchaseItems.reduce((sum, item) => {
                if (!item || item.quantity <= 0) return sum;
                const unit = (Number(item.price) || 0) - (Number(item.discount) || 0);
                return sum + Math.floor(unit * item.quantity);
            }, 0);
    const percentNum = Number(purchase.orderDiscountPercentage);
    const percent = Number.isFinite(percentNum) ? percentNum : null;
    const amountNum = Number(purchase.orderDiscountAmount);
    const effectiveOrderDiscount =
        percent !== null
            ? Math.floor(itemsSubtotal * (percent / 100))
            : Number.isFinite(amountNum)
                ? amountNum
                : 0;

    return (
        <div className="p-6 bg-gray-100 min-h-screen">
            <div className="max-w-7xl mx-auto">
                <h1 className="text-2xl font-bold text-center mb-6">Detalles de la Compra</h1>

                {isCancelled && (
                    <div className="bg-red-50 border border-red-200 p-4 rounded-lg shadow mb-6">
                        <div className="flex items-center justify-between gap-3">
                            <h2 className="text-lg font-semibold text-red-800">Compra anulada</h2>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                                Eliminada
                            </span>
                        </div>
                        <p className="text-sm text-red-700 mt-2">
                            Motivo de anulacion: {cancelReasonText || 'Sin motivo registrado'}
                        </p>
                    </div>
                )}

                <div className="bg-white p-4 rounded-lg shadow mb-6">
                    <h2 className="text-xl font-semibold">Cliente: {purchase.Client?.name}</h2>
                    <p className="text-gray-600">RUT: {purchase.Client?.rut}</p>
                    <p className="text-gray-600">Email: {purchase.Client?.email}</p>
                    <p className="text-gray-600">Teléfono: {purchase.Client?.phone}</p>
                    <p className="text-gray-600">Dirección: {purchase.Client?.address}, {purchase.Client?.city}</p>
                    <div className="mt-3 space-y-1 text-sm">
                        <p className="font-semibold text-gray-800">
                            Subtotal: ${itemsSubtotal.toLocaleString('de-DE')}
                        </p>
                        {effectiveOrderDiscount > 0 && (
                            <p className="text-red-600">
                                Descuento orden
                                {percent != null ? ` (${percent}%)` : ''}: -${effectiveOrderDiscount.toLocaleString('de-DE')}
                            </p>
                        )}
                        <p className="font-semibold text-gray-800">
                            Total: ${purchase.totalAmount.toLocaleString('de-DE')}
                        </p>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">Fecha de compra: {new Date(purchase.createdAt).toLocaleString()}</p>
                    {isCancelled && (
                        <p className="text-sm text-red-700 mt-1">
                            Motivo de anulacion: {cancelReasonText || 'Sin motivo registrado'}
                        </p>
                    )}
                </div>

                {/* 📌 New Payment Details Section */}
                <div className="bg-white p-4 rounded-lg shadow mb-6">
                    <h2 className="text-xl font-semibold">Detalles del Pago</h2>
                    {(() => {
                        const paymentTerm =
                            purchase.paymentTerm === 'credit' || purchase.paymentMethod === 'crédito'
                                ? 'credit'
                                : 'immediate';
                        const paidAmount = Number(purchase.paidAmount || 0);
                        const balance =
                            purchase.balance !== undefined
                                ? Number(purchase.balance || 0)
                                : Math.max((Number(purchase.totalAmount || 0) - paidAmount), 0);
                        const statusLabel = {
                            unpaid: 'Pendiente',
                            partial: 'Parcial',
                            paid: 'Pagado',
                        };
                        const paymentStatusLabel = isCancelled
                            ? 'Eliminada'
                            : statusLabel[purchase.paymentStatus] || 'Pendiente';
                        const dueDateValue = purchase.creditDueDate || purchase.detailForPayment?.creditDueDate;
                        const dueDate = dueDateValue ? new Date(dueDateValue) : null;
                        const isOverdue =
                            paymentTerm === 'credit' &&
                            balance > 0 &&
                            dueDate &&
                            !Number.isNaN(dueDate.getTime()) &&
                            dueDate < new Date();

                        return (
                            <>
                                <p className="text-gray-600 font-semibold">
                                    Condición: <span className="font-normal">{paymentTerm === 'credit' ? 'Crédito' : 'Contado'}</span>
                                </p>
                                {paymentTerm === 'immediate' && (
                                    <p className="text-gray-600 font-semibold">
                                        Método de Pago: <span className="font-normal">{purchase.paymentMethod || 'No especificado'}</span>
                                    </p>
                                )}
                                <p className="text-gray-600 font-semibold">
                                    Estado: <span className="font-normal">{paymentStatusLabel}</span>
                                </p>
                                {isCancelled && (
                                    <p className="text-red-700">
                                        Motivo de anulacion: <span className="font-semibold">{cancelReasonText || 'Sin motivo registrado'}</span>
                                    </p>
                                )}
                                <p className="text-gray-600">
                                    Pagado: <span className="font-semibold">${paidAmount.toLocaleString('de-DE')}</span>
                                </p>
                                <p className="text-gray-600">
                                    Pendiente: <span className="font-semibold">${balance.toLocaleString('de-DE')}</span>
                                </p>
                                {paymentTerm === 'credit' && dueDate && (
                                    <p className={`text-sm ${isOverdue ? 'text-red-600' : 'text-gray-600'}`}>
                                        Vence: {dueDate.toLocaleDateString('es-CL')}
                                        {isOverdue ? ' (Vencido)' : ''}
                                    </p>
                                )}
                            </>
                        );
                    })()}

                    {/* Conditional Display for Each Payment Method */}
                    {purchase.paymentTerm !== 'credit' && purchase.paymentMethod === "cheque" && (
                        <>
                            <p className="text-gray-600">Número de Cheque: <span className="font-semibold">{purchase.detailForPayment?.chequeNumber || 'N/A'}</span></p>
                            <p className="text-gray-600">Fecha de Expiración: <span className="font-semibold">{purchase.detailForPayment?.chequeExpiration || 'N/A'}</span></p>
                            {purchase.detailForPayment?.initialPayment && (
                                <p className="text-gray-600">
                                    Abono Inicial: <span className="font-semibold">${parseInt(purchase.detailForPayment.initialPayment).toLocaleString('de-DE')}</span>
                                </p>
                            )}
                        </>
                    )}

                    {purchase.paymentTerm !== 'credit' && purchase.paymentMethod === "transferencia" && (
                        <p className="text-gray-600">Número de Transacción: <span className="font-semibold">{purchase.detailForPayment?.transactionNumber || 'N/A'}</span></p>
                    )}

                    {purchase.paymentTerm !== 'credit' && purchase.paymentMethod === "efectivo" && (
                        <>
                            <p className="text-gray-600">
                                Monto Recibido: <span className="font-semibold">
                                    ${parseInt(purchase.detailForPayment?.receivedAmount || 0).toLocaleString('de-DE')}
                                </span>
                            </p>
                            <p className="text-gray-600">
                                Vuelto:
                                <span className={`font-semibold ${purchase.detailForPayment?.receivedAmount >= purchase.totalAmount ? 'text-green-600' : 'text-red-600'}`}>
                                    ${parseInt(Math.max((purchase.detailForPayment?.receivedAmount || 0) - purchase.totalAmount, 0)).toLocaleString('de-DE')}
                                </span>
                            </p>
                        </>
                    )}

                    {(purchase.paymentTerm === "credit" || purchase.paymentMethod === "crédito") && (
                        <>
                            <p className="text-gray-600">
                                Fecha de vencimiento del crédito:{' '}
                                <span className="font-semibold">
                                    {purchase.creditDueDate
                                        ? new Date(purchase.creditDueDate).toLocaleDateString('es-CL')
                                        : purchase.detailForPayment?.creditDueDate || purchase.detailForPayment?.creditExpiration || 'N/A'}
                                </span>
                            </p>
                            {(purchase.initialPayment || purchase.detailForPayment?.initialPayment) && (
                                <p className="text-gray-600">
                                    Abono Inicial:{' '}
                                    <span className="font-semibold">
                                        ${Number(purchase.initialPayment || purchase.detailForPayment?.initialPayment || 0).toLocaleString('de-DE')}
                                    </span>
                                </p>
                            )}
                        </>
                    )}

                    {purchase.paymentTerm !== 'credit' && purchase.paymentMethod === "tarjeta" && (
                        <p className="text-gray-600">Número de Transacción: <span className="font-semibold">{purchase.detailForPayment?.transactionNumber || 'N/A'}</span></p>
                    )}

                    {/* If No Payment Details Are Available */}
                    {!purchase.paymentMethod && purchase.paymentTerm !== 'credit' && (
                        <p className="text-red-500">No hay información de pago registrada.</p>
                    )}

                    {Array.isArray(purchase.PurchasePayments) && purchase.PurchasePayments.length > 0 && (
                        <div className="mt-4">
                            <p className="text-gray-700 font-semibold mb-2">Historial de pagos</p>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-gray-500 border-b">
                                            <th className="py-1">Fecha</th>
                                            <th className="py-1">Método</th>
                                            <th className="py-1">Referencia</th>
                                            <th className="py-1 text-right">Monto</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {purchase.PurchasePayments.map((payment) => (
                                            <tr key={payment.id} className="border-b last:border-b-0">
                                                <td className="py-1">
                                                    {payment.paidAt ? new Date(payment.paidAt).toLocaleDateString('es-CL') : '—'}
                                                </td>
                                                <td className="py-1">{payment.method || '—'}</td>
                                                <td className="py-1">{payment.reference || '—'}</td>
                                                <td className="py-1 text-right">
                                                    ${Number(payment.amount || 0).toLocaleString('de-DE')}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                <h2 className="text-lg font-bold mb-4">Productos comprados</h2>
                <div className="bg-white rounded-lg shadow mb-16">
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 text-gray-600">
                                <tr>
                                    <th className="px-3 py-2 text-left font-semibold">Producto</th>
                                    <th className="px-3 py-2 text-left font-semibold">Unidad</th>
                                    <th className="px-3 py-2 text-right font-semibold">Cantidad</th>
                                    <th className="px-3 py-2 text-right font-semibold">Precio</th>
                                    <th className="px-3 py-2 text-right font-semibold">Subtotal</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {purchaseItems.map((item, index) => {
                                    const variety = products
                                        .flatMap((product) => product.Varieties || [])
                                        .find((varietyItem) => varietyItem.id === item.varietyId);
                                    const qty = Number(item.quantity) || 0;
                                    const price = Number(item.price) || 0;
                                    const discount = Number(item.discount) || 0;
                                    const unitPrice = price - discount;
                                    const subtotal = unitPrice * qty;
                                    const hasDiscount = discount > 0;

                                    return (
                                        <tr key={`${item.varietyId}-${item.priceIndex}-${index}`}>
                                            <td className="px-3 py-3 text-left">
                                                <div className="flex items-center gap-3">
                                                    <img
                                                        src={variety?.varietyImage || 'https://placehold.co/64'}
                                                        alt={item.varietyName}
                                                        className="w-10 h-10 rounded object-cover"
                                                        loading="lazy"
                                                    />
                                                    <div className="flex flex-col">
                                                        <span className="font-semibold text-gray-900">
                                                            {item.varietyName}
                                                        </span>
                                                        {hasDiscount && (
                                                            <span className="text-xs text-green-700">
                                                                Descuento: -${discount.toLocaleString('de-DE')}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-3 py-3 text-left text-gray-600">
                                                {item.metricUnit || 'unidad'}
                                            </td>
                                            <td className="px-3 py-3 text-right text-gray-700">
                                                {formatSignificantQuantity(qty)}
                                            </td>
                                            <td className="px-3 py-3 text-right text-gray-700">
                                                {hasDiscount ? (
                                                    <div className="flex flex-col items-end">
                                                        <span className="text-xs text-gray-400 line-through">
                                                            ${price.toLocaleString('de-DE')}
                                                        </span>
                                                        <span className="font-semibold text-green-700">
                                                            ${unitPrice.toLocaleString('de-DE')}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="font-semibold">
                                                        ${price.toLocaleString('de-DE')}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 py-3 text-right font-semibold text-gray-900">
                                                ${subtotal.toLocaleString('de-DE')}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {canReadGuides && deliverySummary && (
                    <div className="bg-white p-4 rounded-lg shadow mb-20">
                        <h2 className="text-lg font-bold mb-2">Resumen de despacho</h2>
                        {(() => {
                            const lines = deliverySummary.summary || [];
                            const totals = lines.reduce(
                                (acc, line) => {
                                    acc.ordered += line.orderedQuantity || 0;
                                    acc.delivered += line.deliveredSoFar || 0;
                                    return acc;
                                },
                                { ordered: 0, delivered: 0 }
                            );
                            const percentage =
                                totals.ordered > 0
                                    ? Math.round((totals.delivered / totals.ordered) * 100)
                                    : 0;
                            return (
                                <p className="text-sm text-gray-700 mb-3">
                                    Progreso total:{' '}
                                    <span className="font-semibold">
                                        {totals.delivered} / {totals.ordered}
                                    </span>{' '}
                                    ({percentage}%)
                                </p>
                            );
                        })()}
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-50">
                                        <th className="px-3 py-2 text-left font-medium text-gray-600">Producto</th>
                                        <th className="px-3 py-2 text-left font-medium text-gray-600">Unidad</th>
                                        <th className="px-3 py-2 text-right font-medium text-gray-600">Pedido</th>
                                        <th className="px-3 py-2 text-right font-medium text-gray-600">Entregado</th>
                                        <th className="px-3 py-2 text-right font-medium text-gray-600">Restante</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(deliverySummary.summary || []).map((line) => (
                                        <tr key={`${line.varietyId}-${line.priceIndex}`} className="border-t">
                                            <td className="px-3 py-2">{line.varietyName}</td>
                                            <td className="px-3 py-2">{line.metricUnit || 'unidad'}</td>
                                            <td className="px-3 py-2 text-right">{line.orderedQuantity}</td>
                                            <td className="px-3 py-2 text-right">
                                                {line.deliveredSoFar}
                                            </td>
                                            <td className={`px-3 py-2 text-right ${line.remainingQuantity > 0 ? 'text-orange-600 font-semibold' : 'text-green-600 font-semibold'}`}>
                                                {line.remainingQuantity}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {(canReadGuides || canCreateGuide) && (
                    <div className="bg-white p-4 rounded-lg shadow mb-24">
                        <div className="flex justify-between items-center mb-3">
                            <h2 className="text-lg font-bold">Guías de despacho</h2>
                            {canCreateGuide && (
                                <button
                                    onClick={() => navigate(`/branches/${branchId}/purchases/${purchase.id}/delivery-guides/new`)}
                                    className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                                >
                                    Crear guía de despacho
                                </button>
                            )}
                        </div>
                        {!canReadGuides ? (
                            <p className="text-sm text-gray-600">No tienes permiso para ver guías de despacho.</p>
                        ) : deliveryGuides.length === 0 ? (
                            <p className="text-sm text-gray-600">No hay guías de despacho para esta compra.</p>
                        ) : (
                            <div className="space-y-2">
                                {deliveryGuides.map((guide) => (
                                    <div
                                        key={guide.id}
                                        className="flex justify-between items-center border rounded-lg px-3 py-2 text-sm bg-gray-50"
                                    >
                                        <div>
                                            <p className="font-semibold">
                                                Guía #{guide.id}{' '}
                                                {guide.guideNumber ? `— Folio ${guide.guideNumber}` : ''}
                                            </p>
                                            <p className="text-gray-600">
                                                Creada el {new Date(guide.createdAt).toLocaleString()}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() =>
                                                navigate(
                                                    `/branches/${branchId}/purchases/${purchase.id}/delivery-guides/${guide.id}/details`
                                                )
                                            }
                                            className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-100 text-sm"
                                        >
                                            Ver / PDF
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <div className="fixed bottom-0 left-0 w-full bg-white p-4 shadow-md flex justify-center gap-4 items-center">
                    <button
                        onClick={() => navigate(`/branches/${branchId}/purchases`)}
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                    >
                        Volver
                    </button>
                    {
                        purchase.clientId && canReadClient &&
                        <button
                            onClick={() => navigate(`/client/${purchase.clientId}`)}
                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                        >
                            Compras Cliente
                        </button>
                    }
                    {canReadSales && (
                        <button
                            onClick={handlePrint}
                            className="px-6 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                        >
                            <FiPrinter size={16} className="inline " />
                        </button>
                    )}
                    {!isClosed && canUpdateSale && (
                        <button
                            onClick={() => navigate(`/branches/${branchId}/purchases/${purchase.id}/edit`)}
                            className="bg-yellow-500 text-white px-3 py-2 rounded hover:bg-yellow-600"
                        >
                            Continuar compra
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PurchaseDetailsPage;
